/**
 * 浏览器 `speechSynthesis` 的**唯一一份**基础适配：支持判定 + 中文语音挑选 + 语音包可用性。
 *
 * ## 为什么单独一个文件
 *
 * 本仓已经有两个地方要念中文：听写播放器（`lib/useDictationPlayer.ts`）与
 * 导入预览的「按顺序朗读」（`components/dictation/importPreviewSpeech.ts`）。
 * 两处各写一份 `pickChineseVoice()` 迟早在某一次改动里漂移（本项目吃过这个亏）。
 * 所以"有没有语音合成 / 挑哪个中文音色 / 这台设备有没有中文语音"只在这里定义。
 *
 * ## 为什么"语音包可用性"是三态（`yes` / `no` / `unknown`）
 *
 * `speechSynthesis.getVoices()` 在 Chrome 上**首次调用经常返回空数组**（语音列表是
 * 异步加载的，列表到了会再发一次 `voiceschanged`）。空数组**不能**当成"没有中文语音"：
 * 否则用户一点「开始朗读」，界面就先报一句"这台设备没有中文语音"，然后语音列表到了
 * 又突然能念了 —— 那比不提示更让人不信任。
 *
 * 所以：
 *   · 有语音且至少一个 `zh` 开头 → `yes`；
 *   · 有语音但一个 `zh` 都没有 → `no`（这时才降级、才提示）；
 *   · 列表还没到（空数组 / 调不通）→ `unknown`（**按"有"处理**，只是可能不是最合适的音色）。
 */
export function isSpeechSynthesisSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'speechSynthesis' in window &&
    typeof SpeechSynthesisUtterance !== 'undefined'
  );
}

/**
 * 挑一个中文语音（`zh` 开头就算；一个都没有返回 `null`）。
 *
 * 拿不到时不阻塞：调用方会把 `lang` 设成 `zh-CN`，多数浏览器会自己挑一个。
 */
export function pickChineseVoice(): SpeechSynthesisVoice | null {
  if (!isSpeechSynthesisSupported()) return null;
  try {
    const voices = window.speechSynthesis.getVoices();
    return voices.find((voice) => voice.lang.toLowerCase().startsWith('zh')) ?? null;
  } catch {
    return null;
  }
}

/** 这台设备有没有中文语音（三态，见文件头）。 */
export type ChineseVoiceSupport = 'yes' | 'no' | 'unknown';

export function chineseVoiceSupport(): ChineseVoiceSupport {
  if (!isSpeechSynthesisSupported()) return 'unknown';
  try {
    const voices = window.speechSynthesis.getVoices();
    /** ⚠️ 空数组 = 列表还没到 ⇒ `unknown`（**不是** `no`）。 */
    if (voices.length === 0) return 'unknown';
    return voices.some((voice) => voice.lang.toLowerCase().startsWith('zh')) ? 'yes' : 'no';
  } catch {
    return 'unknown';
  }
}
